import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format, isToday, isYesterday } from 'date-fns';

const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const useChatMessages = (initialMessages = []) => {
  const [messages, setMessages] = useState(initialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [typingProgress, setTypingProgress] = useState('');

  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date()
    }]);
  }, []);

  return { messages, addMessage, isTyping, setIsTyping, typingProgress, setTypingProgress };
};

const MessageItem = React.memo(({ message, isAi, isTyping, typingProgress }) => {
  const messageTime = useMemo(() =>
    format(message.timestamp, 'h:mm a'),
    [message.timestamp]
  );

  return (
    <div
      className={`message ${isAi ? 'ai-message' : 'user-message'} ${isTyping && isAi ? 'last-message' : ''}`}
      aria-live="polite"
    >
      {isAi && (
        <div className="ai-avatar">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
          </svg>
        </div>
      )}
      <div className="message-content">
        {isAi && <div className="ai-label">Lyrics AI</div>}
        <div className="message-text">
          {(isTyping && isAi ? typingProgress : message.text)
            .split('\n')
            .map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          {isTyping && isAi && <span className="typing-cursor">|</span>}
        </div>
        <div className="message-meta">
          <span className="message-time">{messageTime}</span>
        </div>
      </div>
    </div>
  );
});

MessageItem.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.instanceOf(Date).isRequired
  }).isRequired,
  isAi: PropTypes.bool.isRequired,
  isTyping: PropTypes.bool,
  typingProgress: PropTypes.string
};

const MessageDateSeparator = React.memo(({ date }) => {
  const formattedDate = useMemo(() => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  }, [date]);

  return (
    <div className="date-separator" role="separator">
      <span>{formattedDate}</span>
    </div>
  );
});

MessageDateSeparator.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired
};

const ChatInput = React.memo(({ onSend, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSend(inputValue);
      setInputValue('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="chat-input-container">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Describe the lyrics you want: e.g., 'a hopeful song about stars'..."
        aria-label="Message input"
        className="chat-input"
        disabled={disabled}
        autoFocus
      />
      <button
        type="submit"
        className="send-button"
        disabled={!inputValue.trim() || disabled}
        aria-label="Send message"
      >
        <SendIcon />
      </button>
    </form>
  );
});

ChatInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

const LyricsGeneratorPage = () => {
  const {
    messages,
    addMessage,
    isTyping,
    setIsTyping,
    typingProgress,
    setTypingProgress
  } = useChatMessages([
    {
      id: 'initial-ai-message',
      text: "Hello! I'm your AI Lyrics Generator. Tell me, what kind of lyrics are you looking for today? Give me a theme, mood, or a few keywords!",
      timestamp: new Date(),
      sender: 'ai'
    }
  ]);

  const messagesEndRef = useRef(null);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;

    messages.forEach(message => {
      const messageDate = new Date(message.timestamp);
      messageDate.setHours(0, 0, 0, 0);

      if (!currentDate || currentDate.getTime() !== messageDate.getTime()) {
        currentDate = messageDate;
        groups.push({ date: currentDate, messages: [] });
      }

      groups[groups.length - 1].messages.push(message);
    });

    return groups;
  }, [messages]);

  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, [messages]);


  useEffect(() => {
    if (isTyping && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'ai') {
        const fullText = lastMessage.text;
        setTypingProgress('');

        let i = 0;
        const interval = setInterval(() => {
          setTypingProgress(prev => prev + fullText[i]);
          i++;

          if (i >= fullText.length) {
            clearInterval(interval);
            setIsTyping(false);
          }
        }, 30);

        return () => clearInterval(interval);
      }
    }
  }, [isTyping, messages, setIsTyping, setTypingProgress]);


  const handleSendMessage = useCallback(async (text) => {
    addMessage({
      text,
      sender: 'user'
    });

    setIsTyping(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_GENERATOR_SERVICE_URL}/generate-lyrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: text
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(`Server responded with status ${response.status}: ${errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      const aiResponseText = data.content;

      addMessage({
        text: aiResponseText,
        sender: 'ai'
      });

    } catch (error) {
      console.error('Error generating lyrics:', error);
      addMessage({
        text: "Oops! I encountered an error generating lyrics. Please try again later or refine your request.",
        sender: 'ai'
      });
      setIsTyping(false);
    }
  }, [addMessage, setIsTyping]);

  return (
    <div className="chat-container">
      <header className="chat-header">
        <div className="header-content">
          <div className="ai-avatar large">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
            </svg>
          </div>
          <h1>AI Lyrics Generator</h1>
        </div>
      </header>

      <main className="chat-main">
        <div className="messages-container">
          {groupedMessages.map((group, i) => (
            <React.Fragment key={group.date.getTime()}>
              <MessageDateSeparator date={group.date} />
              {group.messages.map((message, index) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  isAi={message.sender === 'ai'}
                  isTyping={isTyping && index === group.messages.length - 1 && message.sender === 'ai'}
                  typingProgress={typingProgress}
                />
              ))}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-status">
          {isTyping && (
            <div className="typing-indicator">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="ml-2">AI is composing lyrics...</span>
            </div>
          )}
        </div>

        <ChatInput
          onSend={handleSendMessage}
          disabled={isTyping}
        />
      </main>
    </div>
  );
};

export default LyricsGeneratorPage;
